import { Component, type ErrorInfo, type ReactNode } from 'react'
import { RefreshCw } from 'lucide-react'
import { isChunkLoadError, reloadForNewVersion } from '../lib/staleBuild'

/**
 * Zachytí chybu při vykreslení stránky. Nenačtená část kódu po nasazení nové verze → stránka se
 * sama jednou obnoví; jiná chyba (nebo opakovaná) → srozumitelná hláška s tlačítkem místo bílé stránky.
 * `resetKey` (cesta) chybu po přechodu na jinou stránku zahodí.
 */
export default class AppErrorBoundary extends Component<{ children: ReactNode; resetKey?: string; fullScreen?: boolean }, { error: unknown }> {
  state: { error: unknown } = { error: null }

  static getDerivedStateFromError(error: unknown) {
    return { error }
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (isChunkLoadError(error) && reloadForNewVersion()) return
    console.error('Chyba stránky', error, info.componentStack)
  }

  componentDidUpdate(prev: { resetKey?: string }) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null })
  }

  render() {
    if (!this.state.error) return this.props.children
    const stale = isChunkLoadError(this.state.error)
    return (
      <div className={this.props.fullScreen ? 'grid min-h-screen place-items-center bg-slate-50 p-6' : 'grid place-items-center py-16'}>
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-ink-900">{stale ? 'Aplikace byla aktualizována' : 'Stránku se nepodařilo zobrazit'}</h1>
          <p className="mt-2 text-sm text-ink-600">
            {stale ? 'Načtěte stránku znovu, ať máte novou verzi.' : 'Zkuste ji načíst znovu. Pokud se chyba opakuje, dejte nám vědět.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-5 inline-flex h-11 items-center gap-2 rounded-lg bg-brand-600 px-5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700"
          >
            <RefreshCw size={16} /> Načíst znovu
          </button>
        </div>
      </div>
    )
  }
}
